import { call, put, throttle, takeLatest, select, delay } from 'redux-saga/effects'
import jwtDecode from 'jwt-decode'
import qs from 'querystring'
import downloadString from 'lib/download-string'
import { webApi, api } from 'lib/api'
import { errorWrap } from 'lib/saga-helpers'
import { constants, loadUser as loadUserAction } from './actions'
import * as analytics from 'lib/analytics'
import { fetchDropDownNotificationItems, fetchNotifications } from 'store/notifications/actions'
import { resetPostHog } from 'client/lib/posthog'
import { userIsAtLeast } from 'lib/user'
import toast from 'react-hot-toast'
import { errorToast, infoToast, successToast } from 'lib/toast'
import { PLANS } from '../../../server/lib/constants'

const SERVER = typeof window === 'undefined'

export const selectors = {
  referencesToExport: ({ ui }) => ui.referencesToExport
}

export function * getNewToken () {
  const { localStorage } = window
  try {
    const { data: newToken } = yield call(webApi.get, '/auth/api_token')
    localStorage.setItem('apiToken', newToken)
  } catch (e) {
    console.error('could not get token from API, bad scitewebapp JWT?', e)
  }
}

//
// For the case where the user has a token
// not minted by the Node backend.
//
// In this case the token claims are not necesseraly
// derived from the user object. This can be used for
// anonymous users to make claims on restricted API resources,
// and by authenticated users to escalate their permissions.
//
function * loadAnonToken ({ payload: token }) {
  window.sessionStorage.setItem('anonApiToken', token)
}

//
// Finalize user setup once they are loaded from
// express session and have correct API token
//
function * _setupUser (user) {
  yield put(fetchNotifications())
  yield put(fetchDropDownNotificationItems())
  analytics.setUser(user)
}

function * loadUser ({ payload: { query = {} } = {} }) {
  if (SERVER) {
    return
  }
  const { localStorage, location } = window
  const { autologin, ...restQuery } = query
  const currentToken = localStorage?.getItem('apiToken')
  let user = null

  try {
    const res = yield call(webApi.get, '/users/me')
    user = res.data
  } catch (e) {
    const status = e?.response?.status
    if (status === 404) {
      localStorage.removeItem('apiToken')
    }
    yield put({
      type: constants.LOAD_USER_FAILURE,
      error: e
    })
  }

  if (autologin === 'azure' && !userIsAtLeast(user, PLANS.premium)) {
    //
    // Remove autologin from the query so we don't accidentally
    // end up in an infinite loop somehow
    //
    const redirectQuery = qs.stringify(restQuery)
    if (redirectQuery) {
      window.history.replaceState(null, null, window.location.pathname + `?${redirectQuery}`)
    } else {
      window.history.replaceState(null, null, window.location.pathname)
    }
    location.pathname = '/api/auth/azuread'
  }

  if (!user) {
    return
  }

  try {
    const currentClaims = currentToken ? jwtDecode(currentToken) : {}
    const differentUser = currentClaims?.email !== user.email
    const differentPlan = currentClaims?.plan !== user.subscription.plan

    if (currentToken &&
        currentClaims !== null &&
        !differentUser &&
        !differentPlan &&
        currentClaims.version &&
        currentClaims.version === CONFIG.apiWebUser.jwtVersion
    ) {
      yield put({
        type: constants.LOAD_USER_SUCCESS,
        payload: user
      })
      yield _setupUser(user)
      return
    }

    yield getNewToken()
    yield put({
      type: constants.LOAD_USER_SUCCESS,
      payload: user
    })
    yield _setupUser(user)
  } catch (e) {
    yield put({
      type: constants.LOAD_USER_FAILURE,
      error: e
    })
    Rollbar.error('Could not load user', e)
  }
}

function * saveUser ({ payload: { slug, name } }) {
  const { data: savedUser } = yield call(webApi.patch, `/users/${slug}`, { name })
  yield put({ type: constants.SAVE_USER_SUCCESS, data: savedUser })
  yield put(loadUserAction())
}

function * logout () {
  yield call(webApi.get, '/users/logout')
  window.localStorage.removeItem('apiToken')
  yield put({
    type: constants.LOGOUT_SUCCESS
  })

  analytics.event({
    category: 'User',
    action: 'Logged out'
  })
  resetPostHog()
}

function * deleteUser ({ payload: { history, reason } }) {
  // ensure we delete any subscription as well.
  const infoToastId = infoToast({
    message: 'Deleting your account, hang tight...',
    durationMs: 20000
  })

  yield call(webApi.post, '/checkout/subscription/delete', { reason, deleteAccount: true })
  yield call(api.delete, '/users/delete')
  window.localStorage.removeItem('apiToken')
  yield put({
    type: constants.DELETE_USER_SUCCESS
  })

  toast.dismiss(infoToastId)

  successToast({
    message: 'Successfully deleted your account.',
    durationMs: 3000
  })

  history.push('/')
  analytics.event({
    category: 'User',
    action: 'user deleted'
  })
}

function * fetchOnboarding ({ payload: slug }) {
  const { data } = yield call(api.get, `/users/slug/${slug}/onboarding`)

  yield put({
    type: constants.FETCH_ONBOARDING_SUCCESS,
    payload: data
  })
}

export function * getEntitlements () {
  const { data } = yield call(api.get, '/entitlements/entitlement')
  yield put({
    type: constants.GET_ENTITLEMENTS_SUCCESS,
    payload: data
  })
}

export function * saveReferrals ({ payload: { referrals } }) {
  try {
    const { data } = yield call(api.post, '/users/referral', { referrals })
    yield put({
      type: constants.SAVE_REFERRALS_SUCCESS,
      payload: data
    })
  } catch (e) {
    console.error('failed saving referrals for user', e)
    yield put({
      type: constants.SAVE_REFERRALS_FAILURE
    })
  }
  try {
    yield call(webApi.put, '/checkout/subscription/grant-referral-benefits')
    yield loadUser()
  } catch (e) {
    console.error('failed granting referral benefits for user', e)
  }
}

export function * checkOrganizationLicense () {
  const { data: { organizationLicense } } = yield call(webApi.get, '/auth/check_license')
  yield put({
    type: constants.CHECK_PREMIUM_IP_SUCCESS,
    payload: organizationLicense
  })
}

function * redeemCode ({ payload }) {
  const { data } = yield call(webApi.post, '/checkout/subscription/redeem-code', { code: payload })
  if (data.error) {
    yield put({
      type: constants.REDEEM_CODE_ERROR,
      payload: 'An error occurred redeeming the code, please contact support or try again later.'
    })
    return
  }
  analytics.event({
    category: 'Checkout',
    action: 'Redeem Code',
    label: payload
  })
  yield put({
    type: constants.REDEEM_CODE_SUCCESS,
    payload: data
  })
}

function * completeOnboardingStep ({ payload: { category, step, email } }) {
  const res = yield call(api.put, '/users/onboarding', { category, step, email })
  yield put({
    type: constants.LOAD_USER_SUCCESS,
    payload: res.data
  })
}

function * fetchAutocompletes ({ payload: { term, field, includeFields } }) {
  const params = {}

  if (term?.length && term.length > 1000) {
    console.warn('Too many characters to autocomplete')
    return
  }

  if (term) params.prefix = term.slice(0)
  if (field) params.field = field
  if (includeFields) params.include_fields = includeFields

  const { data: { autocompletes } } = yield call(api.get, '/search/autocompletes', { params })

  yield put({
    type: constants.FETCH_AUTOCOMPLETES_SUCCESS,
    payload: { autocompletes, field }
  })
}

function * exportReferences ({ payload: { params } }) {
  const referencesToExport = yield select(selectors.referencesToExport)
  const { data: text, headers } = yield call(
    api.post,
    '/papers/export',
    referencesToExport,
    { params }
  )
  downloadString(text, headers['x-file-name'])
  yield put({ type: constants.EXPORT_REFERENCES_SUCCESS })
}

function * trialActivated () {
  yield call(webApi.post, '/users/activate-trial')
  yield put({
    type: constants.TRIAL_ACTIVATED_SUCCESS
  })
}

function * modalToggled ({ payload: { name } }) {
  const key = `${name}ModalShow`
  const currentValue = yield select(({ ui }) => ui[key])

  if (currentValue) {
    return
  }

  yield delay(600)

  yield put({ type: constants.CLEAR_MODAL_FROM_CONTEXT })
}

export default function * rootSaga () {
  yield takeLatest(constants.TRIAL_ACTIVATED, errorWrap(trialActivated, constants.TRIAL_ACTIVATED_FAILURE))
  yield takeLatest(constants.GET_ENTITLEMENTS, errorWrap(getEntitlements, constants.GET_ENTITLEMENTS_FAILURE))
  yield takeLatest(constants.LOAD_USER, loadUser)
  yield takeLatest(constants.LOGOUT, errorWrap(logout, constants.LOGOUT_FAILURE))
  yield takeLatest(constants.SAVE_USER, errorWrap(saveUser, constants.SAVE_USER_FAILURE))
  yield takeLatest(constants.SAVE_REFERRALS, saveReferrals)
  yield takeLatest(constants.GET_NEW_TOKEN, getNewToken)
  yield takeLatest(constants.CHECK_PREMIUM_IP, errorWrap(checkOrganizationLicense, constants.CHECK_PREMIUM_IP_FAILURE))
  yield takeLatest(
    constants.DELETE_USER,
    errorWrap(
      deleteUser,
      constants.DELETE_USER_FAILURE,
      function * (e) {
        console.error('Unexpected error deleting user: ', e)
        errorToast({
          message: 'Something went wrong deleted your account, try again or contact us at hi@scite.ai',
          durationMs: 4000
        })
      }))
  yield takeLatest(constants.LOAD_ANON_TOKEN, loadAnonToken)
  yield takeLatest(constants.REDEEM_CODE, errorWrap(redeemCode, constants.REDEEM_CODE_ERROR))
  yield takeLatest(constants.COMPLETE_ONBOARDING_STEP, errorWrap(completeOnboardingStep, constants.COMPLETE_ONBOARDING_FAILURE))
  yield takeLatest(
    constants.FETCH_ONBOARDING,
    errorWrap(fetchOnboarding, constants.FETCH_ONBOARDING_FAILURE)
  )

  yield takeLatest(
    constants.EXPORT_REFERENCES,
    errorWrap(exportReferences, constants.EXPORT_REFERENCES_FAILURE)
  )

  yield takeLatest(
    constants.TOGGLE_MODAL,
    modalToggled
  )

  yield throttle(
    300,
    constants.FETCH_AUTOCOMPLETES,
    errorWrap(fetchAutocompletes, constants.FETCH_AUTOCOMPLETES_FAILURE)
  )
}
